import { toast } from "sonner";
import { Link } from "react-router-dom";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Button } from "@/components/ui/button";
import { useUpdateDefaultPronunciations } from "@/hooks/agent/usePronunciationVaults";
import { PronunciationChange } from "@/types/agent/pronunciation-vault";

interface UpdateGeniesSheetProps {
  changes: PronunciationChange[];
  onClose: () => void;
}

export const UpdateGeniesSheet = ({ changes, onClose }: UpdateGeniesSheetProps) => {
  const updateDefaults = useUpdateDefaultPronunciations();

  const handleUpdate = () => {
    updateDefaults.mutate(changes, {
      onSuccess: ({ genieCount }) => {
        toast.success(
          <span>
            Checking {genieCount} {genieCount === 1 ? "genie" : "genies"} for the old pronunciations.{" "}
            <Link to="/admin/bulk-update-jobs" className="underline">Track progress</Link>
          </span>,
        );
        onClose();
      },
      onError: (updateError) => toast.error(updateError.message),
    });
  };

  const changed = changes.filter((change) => change.to !== null);
  const removed = changes.filter((change) => change.to === null);

  return (
    <BaseSheet
      isOpen={changes.length > 0}
      onClose={onClose}
      title="Update existing genies?"
      description="You changed or removed default pronunciations. Genies that already have these words still use the old ones."
      size="lg"
      footer={
        <div className="flex justify-start gap-3">
          <Button variant="outline" onClick={onClose} disabled={updateDefaults.isPending}>
            Not now
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={updateDefaults.isPending}
            className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:brightness-105 text-white border-0"
          >
            {updateDefaults.isPending ? "Starting..." : "Update genies"}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {changed.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700">Changed pronunciations</p>
            {changed.map((change) => (
              <div key={change.word} className="flex justify-between gap-4 text-sm text-gray-600 border-b border-genie-border py-2">
                <span className="font-medium text-gray-900">{change.word}</span>
                <span>
                  <span className="line-through text-gray-400">{change.from}</span> → {change.to}
                </span>
              </div>
            ))}
          </div>
        )}
        {removed.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700">Removed words</p>
            {removed.map((change) => (
              <div key={change.word} className="flex justify-between gap-4 text-sm text-gray-600 border-b border-genie-border py-2">
                <span className="font-medium text-gray-900 line-through">{change.word}</span>
                <span className="line-through text-gray-400">{change.from}</span>
              </div>
            ))}
          </div>
        )}
        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
          <li>Every voice genie is checked, including customers' genies.</li>
          <li>A word is only changed or removed where the genie still uses the old default pronunciation.</li>
          <li>A pronunciation a customer set themselves is kept.</li>
          <li>A removed word that is still in another default vault stays on genies.</li>
          <li>These changes are only known now. If you close this, genies keep the old words.</li>
        </ul>
      </div>
    </BaseSheet>
  );
};
